/*
 * Copyright (c) Domotz Ltd. All rights reserved.
 */

import React from 'react';
import intl from 'react-intl-universal';

import AppNavbar from '../InternetNavbar';
import AppFooter from '../../components/AppFooter';

import NotFound from "../../components/NotFound";
import Loading from "../../components/Loading";

import { scoreDecimals, toCompactAmount } from "../../helpers/IspScoreHelper";
import { renderMetaTagsWithDefImg } from "../../helpers/MetaTagsHelper";

import { linkForPlace } from "../LinkHelper";
import IspSmallCard from "../isp/IspSmallCard";
import ScoreIndicator from "../../components/ScoreIndicator";
import IspCardSummary from "../isp/IspCard";

import DropChart from "../outage/DropChart";
import IspCardOutage from "../outage/OutageCard";

import { getCountryCode, getCountryName } from "./GeoCountries";
import PlaceCarousel from "./PlaceCarousel";
import PlaceHero from "./PlaceHero";
import RegionLevelCountries from "./RegionLevelCountries";

const LEVEL_COUNTRY = 'country';
const LEVEL_REGION = 'region';
const LEVEL_CITY = 'city';

class PlaceOverview extends React.Component {

    constructor(props) {
        super(props);

        let country = this.countryCode(props.match.params);
        let reg = this.regionName(props.match.params);
        let city = this.cityName(props.match.params);
        let level = city ? LEVEL_CITY : reg ? LEVEL_REGION : LEVEL_COUNTRY;

        this.state = {
            countryCode: country,
            regionName: reg,
            cityName: city,
            level: level,
            isps: [],
            ispSortOrder: 0,
            topCitiesInCountry: [],
            topRegionsInCountry: [],
            recentOutages: [],
            dropKpiChart: [],
            isLoading: true,
            isFound: false,
        };

        // This binding is necessary to make `this` work in the callback
        this.sortBySpeed = this.sortBySpeed.bind(this);
        this.sortByRating = this.sortByRating.bind(this);
        this.sortByDistribution = this.sortByDistribution.bind(this);
    }

    countryCode(params) {
        const countryParam = params.country;
        return countryParam.length === 2 ? countryParam : getCountryCode(countryParam);
    }

    regionName(params) {
        const region = params.region;
        return region && region.length > 0 && region !== "all" ? region : null;
    }

    cityName(params) {
        const city = params.city;
        return city && city.length > 0 ? city : null;
    }

    validateRestResponse(response) {
        if (response.status !== 200) {
            throw Error(response.statusText);
        }
        return response;
    }

    catchRestError(response) {
        this.setState({ isLoading: false, isFound: false });
    }

    sortBySpeed(e) {
        e.preventDefault();
        this.setState(state => ({
            ispSortOrder: 0,
            isps: state.isps.sort((i1, i2) => i2.ispScore.scoreValue - i1.ispScore.scoreValue)
        }));
    }

    sortByRating(e) {
        e.preventDefault();
        this.setState(state => ({
            ispSortOrder: 1,
            isps: state.isps.sort((i1, i2) => i2.ispSentiment.sentimentValue - i1.ispSentiment.sentimentValue)
        }));
    }

    sortByDistribution(e) {
        e.preventDefault();
        this.setState(state => ({
            ispSortOrder: 2,
            isps: state.isps.sort((i1, i2) => i2.ispScore.samples - i1.ispScore.samples)
        }));
    }

    componentDidMount() {
        const { countryCode, regionName, cityName, level } = this.state;

        this.mainFetchSequence(countryCode, regionName, cityName, level);
    }


    componentWillReceiveProps(nextProps) {

        if (this.props.match && nextProps && nextProps.match && nextProps.match.params &&
            this.props.match.url !== nextProps.match.url) {

            let country = this.countryCode(nextProps.match.params);
            let reg = this.regionName(nextProps.match.params);
            let city = this.cityName(nextProps.match.params);
            let level = city ? LEVEL_CITY : reg ? LEVEL_REGION : LEVEL_COUNTRY;

            this.mainFetchSequence(country, reg, city, level);

        }
    }

    mainFetchSequence(countryCode, regionName, cityName, level) {

        // No country, no party
        if (!countryCode) {
            this.setState({ isLoading: false, isFound: false });
            return;
        }

        this.setState({ isLoading: true, isFound: false });

        let url = '/rest/internet/isps/';
        switch (level) {
            case LEVEL_CITY:
                url += countryCode + "/" + regionName + "/" + cityName;
                break;
            case LEVEL_REGION:
                url += countryCode + "/" + regionName;
                break;
            default:
                url += countryCode;
        }

        fetch(url)
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then(data => {
                this.setState({ isps: data });

                if (RegionLevelCountries.is(countryCode)) {
                    return fetch('/rest/place/regions/' + countryCode + '?top=13')
                        .then(response => this.validateRestResponse(response))
                        .then(response => response.json())
                        .then(data => {
                            if (level === LEVEL_REGION && regionName) {
                                // Remove this own region from the list to explore
                                const index = data.map(p => p.regionName).indexOf(regionName);
                                if (index >= 0)
                                    data.splice(index, 1);
                            }
                            this.setState({ topRegionsInCountry: data });
                            return fetch('/rest/place/cities/' + countryCode + '?top=13')
                        })
                } else {
                    return fetch('/rest/place/cities/' + countryCode + '?top=13');
                }
            })
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then(data => {
                if (level === LEVEL_CITY && cityName) {
                    // Remove this own city from the list to explore
                    const index = data.map(p => p.cityName).indexOf(cityName);
                    if (index >= 0)
                        data.splice(index, 1);
                }

                this.setState({ topCitiesInCountry: data, isLoading: false, isFound: true });

                return fetch('/rest/internet/outages/' + countryCode + '/' + (regionName ? regionName : 'all') + '?top=12' + (level === LEVEL_CITY ? '&city=' + cityName : ''));
            })
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then(data => {
                this.setState({ recentOutages: data.reports, countryCode: countryCode, regionName, cityName });
                if (typeof window.initSlickCarousel === "function") {
                    window.initSlickCarousel();
                }
            })
            .catch(e => this.catchRestError(e))
            .finally(() => window.scrollTo(0, 0))

        if (level === LEVEL_CITY)
            return;

        // Meanwhile, in parallel: drop KPI
        fetch('/rest/internet/dropkpi/' + countryCode + '/' + (regionName ? regionName : 'all'))
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then(data => {
                this.setState({ dropKpiChart: data.points });
            })
            .catch(e => this.catchRestError(e));

    }

    // --------------------------------------------------------------------------------
    // MAIN RENDERING
    // --------------------------------------------------------------------------------

    render() {
        const { isLoading, isFound, dropKpiChart, countryCode, regionName, cityName } = this.state;

        if (isLoading) {
            return <Loading />;
        }

        if (!isFound) {
            return <NotFound />;
        }


        let placeName = cityName ? cityName : regionName ? regionName : getCountryName(countryCode);

        return (
            <div>
                {renderMetaTagsWithDefImg(intl.get('metatag_place_title', { place: placeName }), intl.get('metatag_place_descr', { place: placeName }))}

                <AppNavbar />

                <PlaceHero countryCode={countryCode} regionName={regionName} cityName={cityName} />
                <main id="content" role="main">

                    {this.renderRecentOutageSection()}

                    {this.renderDropChart(dropKpiChart)}

                    <div className="w-md-80 w-lg-50 text-center mx-md-auto space-top-2">
                        <h2 className="text-primary">{intl.get('place_top_providers_title')}</h2>
                        <p>{intl.get('place_top_providers_subtitle')}</p>
                    </div>

                    <ul className="nav nav-pills nav-borderless justify-content-center">
                        <li className="nav-item">
                            <a className={"nav-link " + (this.state.ispSortOrder === 0 ? "active" : "")}
                                href="/#" onClick={this.sortBySpeed}>{intl.get('place_sort_by_speed')}</a>
                        </li>
                        <li className="nav-item">
                            <a className={"nav-link " + (this.state.ispSortOrder === 1 ? "active" : "")}
                                href="/#" onClick={this.sortByRating}>{intl.get('place_sort_by_rating')}</a>
                        </li>
                        <li className="nav-item">
                            <a className={"nav-link " + (this.state.ispSortOrder === 2 ? "active" : "")}
                                href="/#" onClick={this.sortByDistribution}>{intl.get('place_sort_by_share')}</a>
                        </li>
                    </ul>

                    {this.renderIspGrid()}

                </main>

                <div className="bg-light overflow-hidden">
                    <div className="container space-2">
                        {this.renderBestRegionsSection(this.state.topRegionsInCountry)}
                        {this.renderBestCitiesSection(this.state.topCitiesInCountry)}
                    </div>
                </div>

                <AppFooter />

            </div>
        );
    }

    // --------------------------------------------------------------------------------
    // Render Outage carousel
    // --------------------------------------------------------------------------------


    renderRecentOutageSection() {
        if (!this.state.recentOutages || this.state.recentOutages.length === 0) {
            return <section />
        }

        return (
            <div>
                <div className="container space-2">
                    <section>
                        <div className="w-md-80 w-lg-50 text-center mx-md-auto mb-9">
                            <h2 className="text-primary">{intl.getHTML('home_recent_outages_title')}</h2>
                            <p>{intl.get('home_recent_outages_subtitle')}</p>
                        </div>

                        <div className="js-slick-carousel slick slick-equal-height slick-gutters-3 mb-7"
                            data-hs-slick-carousel-options='{
                                "prevArrow": "<span class=\"fas fa-arrow-left slick-arrow slick-arrow-primary slick-arrow-left slick-arrow-offset slick-arrow-centered-y rounded-circle \"></span>",
                                "nextArrow": "<span class=\"fas fa-arrow-right slick-arrow slick-arrow-primary slick-arrow-right slick-arrow-offset slick-arrow-centered-y rounded-circle \"></span>",
                                "slidesToShow": 3,
                                "dots": true,
                                "dotsClass": "slick-pagination mt-3",
                                "responsive": [{
                                    "breakpoint": 992,
                                        "settings": {
                                    "slidesToShow": 2
                                        }
                                        }, {
                                    "breakpoint": 768,
                                        "settings": {
                                    "slidesToShow": 1
                                        }
                                        }]
                            }'>

                            {this.renderRecentOutageCards()}
                        </div>
                    </section>
                </div>
            </div>
        );
    }

    renderRecentOutageCards() {
        let outages = this.state.recentOutages.slice(0);
        return outages.map(outage => (this.renderOutageCard(outage)));
    }


    renderOutageCard(outage) {
        return <IspCardOutage
            className="js-slide card card-frame shadow-sm mt-1 mb-3"
            outage={outage} />;
    }


    // --------------------------------------------------------------------------------
    // Render drop rate chart
    // --------------------------------------------------------------------------------

    renderDropChart(points) {

        if (!points || points.length === 0) {
            return <section />
        }

        let totProbes = points[points.length - 1].total;
        if (totProbes === 0) {
            return <section />
        }

        if (points.filter(p => (p.value > 0)).length === 0) {
            return <section />
        }

        let geoArea = this.state.level === LEVEL_REGION && RegionLevelCountries.is(this.state.countryCode) ?
            this.state.regionName : getCountryName(this.state.countryCode);


        return (
            <section className="w-md-80 w-lg-50 mx-md-auto text-center space-top-1">
                <h2 className="text-primary">{intl.get('drop_rate_title')}</h2>
                <p>{intl.get('drop_rate_samples', { probeCount: totProbes, geoArea: geoArea })}</p>
                <div className="col-lg-12 mb-2">
                    <DropChart points={points} />
                </div>
            </section>
        );
    }

    renderIspGrid() {
        if (this.state.isps.length === 0) {
            return (
                <section className="container space-2">
                    <p>{intl.get('scoreboard_empty')}</p>
                </section>
            );
        }
        let isps = this.state.isps.slice(0);  // Clone

        let firstRow = null;
        let rows = [];
        while (isps.length > 0) {
            let row = isps.slice(0, 3);
            isps = isps.slice(3);

            if (!firstRow)
                firstRow = row;
            else
                rows.push(row);
        }

        return (
            <section className="container space-2">
                {firstRow.length > 0 ? this.renderIspGridRowTop(firstRow) : ''}
                {firstRow.length > 0 && rows.length > 0 &&
                    <h2 className="h4 font-weight-normal text-primary space-1 text-center">{intl.get('scoreboard_more')}</h2>
                }
                {rows.map((row, index) => this.renderIspGridRow(row, (index + 1) * 3))}
            </section>
        );
    }

    renderIspGridRowTop(isps) {
        return (
            <div
                key={'row_top'}
                className="card-deck d-block d-lg-flex card-lg-gutters-2">
                {isps.map((isp, index) => this.renderIspCard(isp, index))}
                {isps.length < 3 ? new Array(3 - isps.length).fill({}).map((o, index) => this.renderEmptyIspCard(isps.length + index)) : ''}
            </div>
        );
    }

    renderIspCard(isp, index) {
        return (
            <IspCardSummary
                key={'isp_' + index}
                className="card card-frame transition-3d-hover mb-3"
                isp={isp}
                show={this.state.ispSortOrder}
                rank={index + 1}
            />
        );
    }

    renderIspGridRow(isps, offset) {
        return (
            <div
                key={'row_' + offset}
                className="card-deck d-block d-lg-flex card-lg-gutters-2">
                {isps.map((isp, index) => this.renderIspSmallCard(isp, offset + index))}
                {isps.length < 3 ? new Array(3 - isps.length).fill({}).map((o, index) => this.renderEmptyIspCard(offset + isps.length + index)) : ''}
            </div>
        );
    }

    renderIspSmallCard(isp, index) {
        return <IspSmallCard
            key={'isp_' + index}
            isp={isp}
            show={this.state.ispSortOrder}
            rank={index + 1} />
    }

    renderMetricIndicator(isp) {
        switch (this.state.ispSortOrder) {
            case 0:
                const scoreValue = isp.ispScore.scoreValue;
                let score = scoreDecimals(scoreValue / (100.0 / 5), 1);
                return [
                    <ScoreIndicator score={scoreValue} />,
                    <span className="font-weight-semi-bold ml-2 text-body">{score}</span>
                ];
            case 1:
                const sentimentValue = isp.ispSentiment.sentimentValue;
                let sentiment = scoreDecimals(sentimentValue / (100.0 / 5), 1);
                return [
                    <ScoreIndicator score={sentimentValue}
                        fullClass="fas fa-heart"
                        halfClass="fas fa-heart"
                        emptyClass="far fa-heart" />,
                    <span className="font-weight-semi-bold ml-2 text-body">{sentiment}</span>
                ];
            case 2:
                const sampleValue = isp.ispScore.samples;
                return [
                    <span className="text-secondary">{intl.get('scoreboard_samples_amount', { amount: toCompactAmount(sampleValue) })}</span>
                ];
            default:
                return '';
        }

    }

    renderEmptyIspCard(index) {
        return (
            <div key={'empty-' + index}
                className="card card-frame border-0 shadow-none">
            </div>
        );
    }

    // --------------------------------------------------------------------------------
    // Render places carousel
    // --------------------------------------------------------------------------------

    renderBestRegionsSection(itemList) {
        if (itemList.length === 0)
            return <section />;

        const cc = this.state.countryCode;
        // US,AU have states
        // CA has territories+provinces
        // GB has counties (https://en.wikipedia.org/wiki/Countries_of_the_United_Kingdom)
        // IN has states+union territories (https://en.wikipedia.org/wiki/States_and_union_territories_of_India)
        return (
            <section>
                <div className="w-md-80 w-lg-50 text-center mx-md-auto mb-9">
                    <span className="btn btn-xs btn-soft-success btn-pill mb-2">{intl.get('explore_badge')}</span>
                    <h2 className="text-primary">{intl.getHTML(this.state.level === LEVEL_REGION ?
                        'explore_other_region_title' : 'explore_region_title', { country: getCountryName(cc) })}</h2>
                    <p>{intl.get('explore_subtitle')}</p>
                </div>

                <PlaceCarousel items={itemList}
                    makeTitle={place => place.regionName}
                    makeLink={place => linkForPlace(getCountryName(place.countryCode), place.regionName)} />
            </section>
        );
    }

    renderBestCitiesSection(itemList) {
        if (itemList.length === 0)
            return <section />;

        const cc = this.countryCode(this.props.match.params);
        return (
            <section>
                <div className="w-md-80 w-lg-50 text-center mx-md-auto mb-9">
                    <span className="btn btn-xs btn-soft-success btn-pill mb-2">{intl.get('explore_badge')}</span>
                    <h2 className="text-primary">{intl.getHTML(this.state.level === LEVEL_CITY ?
                        'explore_other_city_title' : 'explore_city_title', { country: getCountryName(cc) })}</h2>
                    <p>{intl.get('explore_subtitle')}</p>
                </div>

                <PlaceCarousel
                    items={itemList}
                    makeTitle={place => place.cityName}
                    makeLink={place => linkForPlace(getCountryName(place.countryCode), place.regionName, place.cityName)} />
            </section>
        );
    }


}

export default PlaceOverview;